import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiTrophy, FiStar, FiUsers, FiCheckCircle, FiArrowRight, FiGift, FiAward, FiHeart, FiTrendingUp, FiDollarSign, FiMessageCircle, FiZap, FiTarget, FiChevronDown, FiChevronUp } = FiIcons;

function App() {
  const [showConfetti, setShowConfetti] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(50)].map((_, i) => (
            <div key={i} className={`confetti confetti-${i % 5}`}></div>
          ))}
        </div>
      )}

      {/* Sticky CTA Mobile */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-gradient-to-r from-amber-400 to-orange-500 p-4 shadow-2xl">
        <motion.a
          href="https://forms.gle/eVFoLbDqf7dy9cNu8"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full bg-white text-amber-600 font-bold py-3 px-4 rounded-xl text-center shadow-lg"
          whileTap={{ scale: 0.95 }}
        >
          👉 Apply Now (Hall of Fame + Bonuses)
        </motion.a>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 opacity-95"></div>
        <div className="absolute inset-0">
          <div className="hero-pattern"></div>
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8"
          >
            <SafeIcon icon={FiTrophy} className="text-6xl text-amber-400 mx-auto mb-4" />
          </motion.div>

          <motion.h1
            {...fadeInUp}
            className="text-4xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            🏆 Become a <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500">Hall of Fame Champion</span>
            <br />— Unlock Lifetime Rewards
          </motion.h1>

          <motion.p
            {...fadeInUp}
            transition={{ delay: 0.2 }}
            className="text-xl lg:text-2xl mb-8 text-blue-100 max-w-4xl mx-auto leading-relaxed"
          >
            When you achieve your first <span className="text-amber-400 font-bold">₹1 Lakh</span> using FD + DMC systems, you enter our Hall of Fame and unlock exclusive lifetime bonuses — starting with <span className="text-amber-400 font-bold">FREE WABA access forever</span>.
          </motion.p>

          <motion.div
            {...fadeInUp}
            transition={{ delay: 0.4 }}
            className="flex flex-col lg:flex-row gap-4 justify-center items-center"
          >
            <motion.a
              href="https://forms.gle/eVFoLbDqf7dy9cNu8"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold py-4 px-8 rounded-2xl text-lg shadow-2xl hover:shadow-amber-500/25 transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🌟 Apply for Hall of Fame Now
              <SafeIcon icon={FiArrowRight} />
            </motion.a>
            
            <motion.button
              className="bg-white/10 backdrop-blur-sm text-white font-semibold py-4 px-8 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
            >
              🎁 Unlock Your Free Bonuses
              <SafeIcon icon={FiGift} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Why Hall of Fame Matters */}
      <section className="py-20 px-4 bg-gradient-to-r from-slate-900 to-blue-900">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            {...fadeInUp}
            className="text-white"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-8 text-amber-400">This is not just an award.</h2>
            <p className="text-xl lg:text-2xl mb-12 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              This is a moment where your hard work is recognised by thousands in our FD & DMC family.
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { icon: FiUsers, text: "You inspire new members" },
              { icon: FiAward, text: "You become part of the FD success legacy" },
              { icon: FiStar, text: "You get the rare honour only achievers receive" },
              { icon: FiTarget, text: "You become a case study featured in our trainings" },
              { icon: FiGift, text: "You unlock lifetime privileges and tools" }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20"
              >
                <SafeIcon icon={item.icon} className="text-3xl text-amber-400 mb-4 mx-auto" />
                <p className="text-white font-medium">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            {...fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 text-slate-900">
              What You Get When You Hit <span className="text-amber-500">₹1 Lakh</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Exclusive lifetime benefits that keep giving back to our Hall of Fame champions
            </p>
          </motion.div>

          <motion.div
            variants={staggerChildren}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-2 gap-8"
          >
            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-3xl shadow-xl border border-amber-200"
            >
              <SafeIcon icon={FiMessageCircle} className="text-4xl text-amber-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Lifetime FREE WABA Access</h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  ₹0 monthly fee
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  ₹0 markup
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Only Meta's original charges
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Unlimited users, contacts, workflows
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  First 1000 conversations FREE every month
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-3xl shadow-xl border border-blue-200"
            >
              <SafeIcon icon={FiTrophy} className="text-4xl text-blue-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Hall of Fame Trophy & Certificate</h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Your name, your achievement
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Featured in the Community Spotlight
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Your photo displayed in our Hall of Fame Page
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-3xl shadow-xl border border-green-200"
            >
              <SafeIcon icon={FiGift} className="text-4xl text-green-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Exclusive Bonuses (Free)</h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Success interview
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Case study creation
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Priority feature access
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Success mentor community
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Spotlight on social channels
                </li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-3xl shadow-xl border border-purple-200"
            >
              <SafeIcon icon={FiHeart} className="text-4xl text-purple-500 mb-6" />
              <h3 className="text-2xl font-bold mb-4 text-slate-900">Mentorship Recognition</h3>
              <ul className="space-y-3 text-slate-700">
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Special badge
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Priority support
                </li>
                <li className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                  Leadership seat in future events
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Journey Timeline */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-100 to-blue-100">
        <div className="max-w-6xl mx-auto">
          <motion.div
            {...fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 text-slate-900">
              The FD Hall of Fame Journey
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Your path from joining our community to becoming a celebrated champion
            </p>
          </motion.div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-amber-400 to-orange-500 hidden lg:block"></div>
            
            {[
              { step: 1, title: "Join FD + DMC Community", icon: FiUsers },
              { step: 2, title: "Use Funnels + WhatsApp Automation", icon: FiZap },
              { step: 3, title: "Launch Micro WhatsApp Course", icon: FiMessageCircle },
              { step: 4, title: "Close Your First Sales", icon: FiDollarSign },
              { step: 5, title: "Hit Your First ₹1 Lakh", icon: FiTrendingUp },
              { step: 6, title: "Apply for Hall of Fame", icon: FiTarget },
              { step: 7, title: "Get Awarded", icon: FiAward },
              { step: 8, title: "Unlock Lifetime FREE WABA + Bonuses", icon: FiTrophy }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -100 : 100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center mb-12 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
              >
                <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:pr-8' : 'lg:pl-8'}`}>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-slate-200">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
                        {item.step}
                      </div>
                      <SafeIcon icon={item.icon} className="text-2xl text-amber-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                  </div>
                </div>
                
                <div className="hidden lg:block w-2/12 flex justify-center">
                  <div className="w-4 h-4 bg-amber-400 rounded-full border-4 border-white shadow-lg"></div>
                </div>
                
                <div className="hidden lg:block w-5/12"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            {...fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 text-slate-900">
              Why Choose FD Official WABA?
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Compare and see the unmatched value we offer to our Hall of Fame members
            </p>
          </motion.div>

          <motion.div
            {...fadeInUp}
            className="overflow-x-auto"
          >
            <table className="w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
              <thead className="bg-gradient-to-r from-slate-900 to-blue-900 text-white">
                <tr>
                  <th className="py-4 px-6 text-left">Feature</th>
                  <th className="py-4 px-6 text-center">WATI</th>
                  <th className="py-4 px-6 text-center">AiSensy</th>
                  <th className="py-4 px-6 text-center bg-gradient-to-r from-amber-500 to-orange-500">FD Official WABA (Platinum)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { feature: "Monthly Fee", wati: "₹2,499", aisensy: "₹1,500", fd: "₹0" },
                  { feature: "Users", wati: "3", aisensy: "5", fd: "Unlimited" },
                  { feature: "Contacts Limit", wati: "Limited", aisensy: "Limited", fd: "Unlimited" },
                  { feature: "Workflows", wati: "Limited", aisensy: "Limited", fd: "Unlimited" },
                  { feature: "Markup Fee", wati: "YES", aisensy: "YES", fd: "NO" },
                  { feature: "Meta Actual Price", wati: "Hidden", aisensy: "Hidden", fd: "Original Price Only" },
                  { feature: "First 1000 Conversations", wati: "Paid", aisensy: "Paid", fd: "FREE" },
                  { feature: "Hall of Fame Bonus", wati: "No", aisensy: "No", fd: "YES" }
                ].map((row, index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                    <td className="py-4 px-6 font-semibold text-slate-900">{row.feature}</td>
                    <td className="py-4 px-6 text-center text-slate-600">{row.wati}</td>
                    <td className="py-4 px-6 text-center text-slate-600">{row.aisensy}</td>
                    <td className="py-4 px-6 text-center font-bold text-amber-600 bg-amber-50">{row.fd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Why We Offer Free WABA */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-900 to-indigo-900">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl lg:text-5xl font-bold mb-8 text-amber-400">
              Why We Offer Lifetime FREE WABA?
            </h2>
            
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-3xl mb-8">
              <p className="text-xl lg:text-2xl mb-6 text-blue-100">
                Because we want you to focus on:
              </p>
              <div className="flex flex-col lg:flex-row gap-6 justify-center items-center mb-6">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-400" />
                  <span className="text-lg font-semibold">Growth</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-400" />
                  <span className="text-lg font-semibold">Revenue</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiCheckCircle} className="text-green-400" />
                  <span className="text-lg font-semibold">Impact</span>
                </div>
              </div>
              <p className="text-lg text-blue-200">—not on subscription fees.</p>
            </div>

            <div className="bg-amber-400/20 backdrop-blur-sm p-6 rounded-2xl border border-amber-400/30">
              <blockquote className="text-xl lg:text-2xl font-medium italic text-amber-100">
                "When our members win, our community becomes stronger."
              </blockquote>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            {...fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 text-slate-900">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Everything you need to know about the Hall of Fame program
            </p>
          </motion.div>

          <div className="space-y-4">
            {[
              {
                q: "Who is eligible?",
                a: "Active FD Platinum members who crossed ₹1 Lakh using FD & DMC methods."
              },
              {
                q: "What proof should I submit?",
                a: "Screenshot of sales, CRM, gateway reports, or WhatsApp conversion."
              },
              {
                q: "Is the WABA access really lifetime free?",
                a: "YES, once you achieve Hall of Fame status and remain an active member."
              },
              {
                q: "How long does approval take?",
                a: "2–5 working days."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full p-6 text-left flex justify-between items-center hover:bg-slate-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-slate-900">{faq.q}</h3>
                  <SafeIcon 
                    icon={openFaq === index ? FiChevronUp : FiChevronDown} 
                    className="text-amber-500 text-xl"
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-600">{faq.a}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500">
        <div className="max-w-4xl mx-auto text-center text-white">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl lg:text-6xl font-bold mb-8 leading-tight">
              🌟 Take the Next Step — Become a Hall of Fame Champion
            </h2>
            
            <p className="text-xl lg:text-2xl mb-12 leading-relaxed">
              You've already done the hard work.<br />
              You've already invested your time, energy & trust in FD.<br />
              <span className="font-bold">Now it's your moment to get recognised, celebrated, and rewarded.</span>
            </p>

            <motion.a
              href="https://forms.gle/eVFoLbDqf7dy9cNu8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-orange-500 font-bold py-6 px-12 rounded-3xl text-xl shadow-2xl hover:shadow-white/25 transition-all duration-300 mb-12"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              🔥 Apply for Hall of Fame & Unlock Free Bonuses
            </motion.a>

            <div className="bg-white/20 backdrop-blur-sm p-8 rounded-3xl">
              <p className="text-xl lg:text-2xl mb-6 font-medium">
                "Every Hall of Fame member was once where you are today.<br />
                One decision changed their life."
              </p>
              <p className="text-2xl font-bold">Today that decision is yours.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-900 text-white text-center">
        <motion.a
          href="https://forms.gle/eVFoLbDqf7dy9cNu8"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-gradient-to-r from-amber-400 to-orange-500 text-white font-bold py-4 px-8 rounded-2xl text-lg shadow-2xl hover:shadow-amber-500/25 transition-all duration-300"
          whileHover={{ scale: 1.05 }}
        >
          💛 Apply Now & Claim Your Free Bonuses
        </motion.a>
        <p className="mt-8 text-slate-400">
          👉 <a href="https://forms.gle/eVFoLbDqf7dy9cNu8" className="text-amber-400 hover:underline">https://forms.gle/eVFoLbDqf7dy9cNu8</a>
        </p>
      </footer>
    </div>
  );
}

export default App;